import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import { useSupabase } from "../../auth/useSupabase";
import { teamService } from "../../data/teamService";
import leagueService from "../../data/leagueService";
import { StandardButton } from "../../components/StandardButton";
import { EditTeamDialog } from "../game-builder/EditTeamDialog";
import { convertHeicToJpeg, isHeicImage } from "../useRoundAttachments";
import { useNotification } from "../../contexts/NotificationContext";
import { App } from "../../types/types";

interface TeamListItemProps {
  team: App.TeamWithMembers;
  isOwner: boolean;
  currentUserMembership: App.LeagueMemberWithProfile | null;
  allLeagueMembers: App.LeagueMemberWithProfile[];
  unassignedMembers: App.LeagueMemberWithProfile[];
  onDelete: (teamId: string) => void;
  onSaveTeam: (updatedTeam: App.TeamWithMembers) => void;
  onMemberChange: () => void;
}

const TeamListItem = (props: TeamListItemProps) => {
  const {
    team: teamProp,
    isOwner,
    currentUserMembership,
    allLeagueMembers,
    unassignedMembers,
    onDelete,
    onMemberChange,
  } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [team, setTeam] = useState<App.TeamWithMembers>(teamProp);
  const [loadingLogo, setLoadingLogo] = useState<boolean>(true);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [addMemberAnchor, setAddMemberAnchor] = useState<HTMLElement | null>(
    null,
  );

  const { showSnackbar } = useNotification();

  const { supabase, user } = useSupabase();

  // Keep local team state in sync with prop
  useEffect(() => {
    setTeam(teamProp);
  }, [teamProp]);

  const fetchLogo = useCallback(async () => {
    try {
      if (!team.logo_id) {
        setLoadingLogo(false);
        return;
      }
      setLoadingLogo(true);
      const { data, error } = await supabase.storage
        .from("dozen-attachments")
        .createSignedUrl(`attachments/${team.logo_id}`, 3600);

      if (error) {
        console.error("Error fetching logo:", error);
        return;
      }

      if (data) {
        setLogoPreview(data.signedUrl);
      } else {
        setLogoPreview(null);
      }
    } catch (err) {
      console.error("Error in fetchLogo:", err);
    } finally {
      setLoadingLogo(false);
    }
  }, [team.logo_id, supabase]);

  useEffect(() => {
    fetchLogo();
  }, [fetchLogo]);

  const handleEditOpen = () => {
    setIsEditing(true);
    if (team.logo_id) {
      fetchLogo();
    }
  };

  const handleEditClose = () => {
    setIsEditing(false);
  };

  const handleDeleteOpen = () => {
    setIsDeleting(true);
  };

  const handleDeleteClose = () => {
    setIsDeleting(false);
  };

  const uploadLogo = async (logoFile: File | null): Promise<string | null> => {
    if (!logoFile) return team.logo_id;

    try {
      let processedFile = logoFile;

      // Convert HEIC files to JPEG
      if (isHeicImage(logoFile)) {
        try {
          showSnackbar(
            `Converting ${logoFile.name} from HEIC to JPEG...`,
            "info",
          );

          processedFile = await convertHeicToJpeg(logoFile);

          showSnackbar(
            `Successfully converted ${logoFile.name} to JPEG`,
            "success",
          );
        } catch (error) {
          showSnackbar(
            `Failed to convert HEIC file: ${logoFile.name}`,
            "error",
          );
          return null; // Skip upload if conversion fails
        }
      }

      // Create a unique filename
      const fileExt =
        processedFile.name.split(".").pop()?.toLowerCase() || "jpg";
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 15)}.${fileExt}`;
      const filePath = `attachments/${fileName}`;

      // Upload the file to Supabase
      const { error } = await supabase.storage
        .from("dozen-attachments")
        .upload(filePath, processedFile);

      if (error) {
        console.error("Error uploading logo:", error);
        return null;
      }

      return fileName; // file name is used as the key to access the logo
    } catch (err) {
      console.error("Error in uploadLogo:", err);
      return null;
    }
  };

  const handleSave = async (
    editedTeam: App.TeamWithMembers,
    logoFile: File | null,
  ) => {
    try {
      // Upload logo if changed
      const logoId = await uploadLogo(logoFile);

      // Update team with logo ID
      const updatedTeam = {
        ...editedTeam,
        logo_id: logoId,
        updated_at: new Date().toISOString(),
      };

      await teamService.update(supabase, team.id, updatedTeam);
      handleEditClose();
      setTeam(updatedTeam);
      props.onSaveTeam(updatedTeam);
    } catch (err) {
      console.error("Error saving team:", err);
    }
  };

  const handleDelete = async () => {
    try {
      // Delete logo from storage if exists
      if (team.logo_id) {
        await supabase.storage
          .from("dozen-attachments")
          .remove([`attachments/${team.logo_id}`]);
      }

      // Delete team
      await teamService.delete(supabase, team.id);

      // Call parent delete handler if provided
      if (onDelete) {
        onDelete(team.id);
      }

      handleDeleteClose();
    } catch (err) {
      console.error("Error deleting team:", err);
    }
  };

  const isCurrentUserOnThisTeam =
    currentUserMembership?.team_id === team.id;

  const handleJoinTeam = async () => {
    if (!currentUserMembership) return;
    try {
      await leagueService.updateMemberTeam(
        supabase,
        currentUserMembership.id,
        team.id,
      );
      showSnackbar(`Joined ${team.name}!`, "success");
      onMemberChange();
    } catch (err) {
      console.error("Error joining team:", err);
      showSnackbar("Failed to join team", "error");
    }
  };

  const handleLeaveTeam = async () => {
    if (!currentUserMembership) return;
    try {
      await leagueService.updateMemberTeam(
        supabase,
        currentUserMembership.id,
        null,
      );
      showSnackbar(`Left ${team.name}`, "success");
      onMemberChange();
    } catch (err) {
      console.error("Error leaving team:", err);
      showSnackbar("Failed to leave team", "error");
    }
  };

  const handleRemoveMember = async (member: App.LeagueMemberWithProfile) => {
    try {
      await leagueService.updateMemberTeam(supabase, member.id, null);
      showSnackbar(`Removed ${member.profile.name} from ${team.name}`, "success");
      onMemberChange();
    } catch (err) {
      console.error("Error removing member:", err);
      showSnackbar("Failed to remove member", "error");
    }
  };

  const handleAssignMember = async (member: App.LeagueMemberWithProfile) => {
    try {
      await leagueService.updateMemberTeam(supabase, member.id, team.id);
      showSnackbar(
        `Added ${member.profile.name} to ${team.name}`,
        "success",
      );
      setAddMemberAnchor(null);
      onMemberChange();
    } catch (err) {
      console.error("Error assigning member:", err);
      showSnackbar("Failed to add member", "error");
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          borderLeft: `6px solid ${team.color}`,
          backgroundColor: "background.paper",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: 2,
            borderColor: team.color,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Logo Display */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 3,
              flexShrink: 0,
              backgroundColor: "background.default",
              overflow: "hidden",
            }}
          >
            {loadingLogo ? (
              <CircularProgress size={30} />
            ) : logoPreview ? (
              <Box
                component="img"
                src={logoPreview}
                alt={`${team.name} logo`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32,
                  fontWeight: "bold",
                  color: team.color,
                }}
              >
                {team.name.charAt(0).toUpperCase()}
              </Box>
            )}
          </Box>

          {/* Team Info */}
          <Box sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                fontSize: 24,
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {team.name}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: team.color,
                  mr: 1,
                }}
              />
              <Box sx={{ fontSize: 14, color: "text.secondary" }}>
                {team.color}
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {/* Join/Leave buttons for current user */}
            {currentUserMembership && !isCurrentUserOnThisTeam && (
              <StandardButton
                variant="outlined"
                startIcon={<LoginIcon />}
                onClick={handleJoinTeam}
                size="large"
                sx={{ minWidth: 100 }}
              >
                Join
              </StandardButton>
            )}
            {currentUserMembership && isCurrentUserOnThisTeam && (
              <StandardButton
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLeaveTeam}
                size="large"
                sx={{ minWidth: 100 }}
              >
                Leave
              </StandardButton>
            )}

            {/* Owner-only: Add member */}
            {isOwner && unassignedMembers.length > 0 && (
              <>
                <StandardButton
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={(e) => setAddMemberAnchor(e.currentTarget)}
                  size="large"
                  sx={{ minWidth: 100 }}
                >
                  Add
                </StandardButton>
                <Menu
                  anchorEl={addMemberAnchor}
                  open={Boolean(addMemberAnchor)}
                  onClose={() => setAddMemberAnchor(null)}
                >
                  {unassignedMembers.map((member) => (
                    <MenuItem
                      key={member.id}
                      onClick={() => handleAssignMember(member)}
                    >
                      {member.profile.name}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* Owner-only: Edit/Delete */}
            {isOwner && (
              <>
                <StandardButton
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditOpen}
                  size="large"
                  sx={{ minWidth: 100 }}
                >
                  Edit
                </StandardButton>
                <StandardButton
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteOpen}
                  size="large"
                  sx={{ minWidth: 100 }}
                >
                  Delete
                </StandardButton>
              </>
            )}
          </Box>
        </Box>

        {/* Members Display */}
        {team.members.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mt: 2,
              pt: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {team.members.map((member) => (
              <Chip
                key={member.id}
                label={member.name}
                variant={member.id === user?.id ? "filled" : "outlined"}
                color={member.id === user?.id ? "primary" : "default"}
                size="small"
                onDelete={
                  isOwner
                    ? () => {
                        const leagueMember = allLeagueMembers.find(
                          (lm) => lm.user_id === member.id,
                        );
                        if (leagueMember) handleRemoveMember(leagueMember);
                      }
                    : undefined
                }
                deleteIcon={isOwner ? <PersonRemoveIcon /> : undefined}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Edit Dialog */}
      <EditTeamDialog
        open={isEditing}
        team={team}
        logoPreview={logoPreview}
        onClose={() => {
          handleEditClose();
          setTeam(teamProp);
        }}
        onSave={handleSave}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleting} onClose={handleDeleteClose}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the team "{team.name}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <StandardButton onClick={handleDeleteClose}>Cancel</StandardButton>
          <StandardButton
            onClick={handleDelete}
            color="error"
            variant="contained"
          >
            Delete
          </StandardButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamListItem;
